/**
 * Historical games browser. Default: last 7 days, all teams. A "Cardinals"
 * preset chip filters to team_id=138; other team chips can be added by
 * extending TEAM_PRESETS. Clicking a card navigates to
 * /games/{game_pk}?mode=historical — the same GameView page renders a
 * frozen snapshot via the historical-mode useGameStream branch.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { useHistoricalGames } from '../api/games';
import { MLB_TEAMS, findTeamById } from '../state/mlbTeams';
import styles from './HistoricalGameList.module.css';

interface TeamPreset {
  id: number;
  label: string;
  abbr: string;
}

// Starting chips — the dropdown lets the user pin any of the other 25 clubs
// at runtime. team_id values from MLB Stats API.
const DEFAULT_PRESETS: TeamPreset[] = [
  { id: 138, label: 'Cardinals', abbr: 'STL' },
  { id: 141, label: 'Blue Jays', abbr: 'TOR' },
  { id: 116, label: 'Tigers', abbr: 'DET' },
  { id: 118, label: 'Royals', abbr: 'KC' },
  { id: 158, label: 'Brewers', abbr: 'MIL' },
];

function ymd(d: Date): string {
  const y = d.getUTCFullYear();
  const m = `${d.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${d.getUTCDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const start = new Date(now);
  start.setUTCDate(start.getUTCDate() - 7);
  return { startDate: ymd(start), endDate: ymd(now) };
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function HistoricalGameList(): JSX.Element {
  const [range, setRange] = useState(defaultRange);
  const [teamId, setTeamId] = useState<number | undefined>(undefined);
  const [presets, setPresets] = useState<TeamPreset[]>(DEFAULT_PRESETS);
  const query = useMemo(() => ({ ...range, teamId }), [range, teamId]);
  const { data: games, isLoading, isError, error } = useHistoricalGames(query);

  function handleDropdownPick(idStr: string): void {
    if (!idStr) return;
    const id = Number(idStr);
    if (!Number.isFinite(id) || id <= 0) return;
    const team = findTeamById(id);
    if (!team) return;
    if (!presets.some((p) => p.id === id)) {
      setPresets((prev) => [...prev, { id, label: team.fullName.split(' ').slice(-1)[0]!, abbr: team.abbr }]);
    }
    setTeamId(id);
  }

  return (
    <>
      <TopBar />
      <main className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>Historical games</h1>
          <p className={styles.subtitle}>
            Final games from the captured window. Click any card to walk through the game
            inning-by-inning.
          </p>
        </header>

        <section className={styles.controls} aria-label="Filters">
          <label className={styles.field}>
            <span className={styles.fieldLabel}>From</span>
            <input
              type="date"
              value={range.startDate}
              onChange={(e) => setRange((r) => ({ ...r, startDate: e.target.value }))}
              className={styles.input}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>To</span>
            <input
              type="date"
              value={range.endDate}
              onChange={(e) => setRange((r) => ({ ...r, endDate: e.target.value }))}
              className={styles.input}
            />
          </label>
          <div className={styles.chips} role="group" aria-label="Team presets">
            <button
              type="button"
              onClick={() => setTeamId(undefined)}
              className={`${styles.chip} ${teamId === undefined ? styles.chipActive : ''}`}
              data-testid="historical-team-all"
            >
              All teams
            </button>
            {presets.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTeamId(t.id)}
                className={`${styles.chip} ${teamId === t.id ? styles.chipActive : ''}`}
                data-testid={`historical-team-${t.abbr}`}
              >
                {t.label}
              </button>
            ))}
            <label className={styles.dropdownLabel}>
              <span className={styles.dropdownLabelText}>Add team</span>
              <select
                className={styles.dropdown}
                value=""
                onChange={(e) => handleDropdownPick(e.target.value)}
                data-testid="historical-team-dropdown"
              >
                <option value="">Pick a team…</option>
                {MLB_TEAMS.map((t) => {
                  const pinned = presets.some((p) => p.id === t.id);
                  return (
                    <option key={t.id} value={t.id} disabled={pinned}>
                      {t.fullName}
                      {pinned ? ' (pinned)' : ''}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>
        </section>

        {isLoading && <p className={styles.loading}>Loading…</p>}
        {isError && (
          <p className={styles.error} role="alert">
            Could not load historical games — {(error as Error)?.message ?? 'unknown error'}
          </p>
        )}
        {games && games.length === 0 && (
          <p className={styles.empty} data-testid="historical-empty">
            No Final games in this window for the selected team.
          </p>
        )}
        {games && games.length > 0 && (
          <ul className={styles.grid} data-testid="historical-list">
            {games.map((g) => (
              <li key={g.game_pk}>
                <Link
                  to={`/games/${g.game_pk}?mode=historical`}
                  className={styles.card}
                  data-testid={`historical-card-${g.game_pk}`}
                >
                  <div className={styles.cardDate}>{formatDate(g.game_date)}</div>
                  <div className={styles.cardMatchup}>
                    <span className={styles.cardTeam}>{g.away}</span>
                    <span className={styles.cardAt}>@</span>
                    <span className={styles.cardTeam}>{g.home}</span>
                  </div>
                  <div className={styles.cardScore}>
                    <span>{g.away_score}</span>
                    <span className={styles.cardDash}>–</span>
                    <span>{g.home_score}</span>
                  </div>
                  <div className={styles.cardStatus}>Final</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </>
  );
}
